// src/admin/subjects/SubjectList.js
import * as React from "react";
import {
    List,
    Datagrid,
    TextField,
    DateField,
    SelectInput,
    useRecordContext,
} from "react-admin";
import { Box, Button, Tooltip, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";
import { adminPatch } from "../../config/api";
import StatusField from "../../components/StatusField";
import ResourceStatusHeader from "../../components/ResourceStatusHeader";
import useModerationAction from "../../hooks/useModerationAction";
import { useResourceSearchFilter } from "../../hooks/useResourceSearch";

/* ================= Action Buttons ================= */
function ValidateSubjectBtn() {
    const record = useRecordContext();
    const { loading, run } = useModerationAction();

    if (!record) return null;
    // Afficher "Valider" seulement si le subject n'est PAS déjà VALIDATED
    if (record.status === "VALIDATED") return null;

    const onClick = async (event) => {
        event?.stopPropagation?.();
        await run({
            id: record.id,
            request: () => adminPatch(`/subjects/${record.id}/validate`),
            successMessage: "Subject validé",
        });
    };

    return (
        <Tooltip title="Valider ce subject">
      <span>
        <Button
            onClick={onClick}
            disabled={loading}
            variant="contained"
            size="small"
            sx={{
                bgcolor: "#2e7d32",
                color: "white",
                borderRadius: "18px",
                textTransform: "none",
                px: 1.5,
                py: 0.25,
                "&:hover": { bgcolor: "#1b5e20" },
            }}
            startIcon={
                loading ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                ) : (
                    <CheckIcon fontSize="small" />
                )
            }
        >
          Valider
        </Button>
      </span>
        </Tooltip>
    );
}

function RejectSubjectBtn() {
    const record = useRecordContext();
    const { loading, run } = useModerationAction();

    if (!record) return null;
    // Afficher "Rejeter" tant que le sujet n'est pas déjà rejeté
    if (record.status === "REJECTED") return null;

    const onClick = async (event) => {
        event?.stopPropagation?.();
        await run({
            id: record.id,
            request: () =>
                adminPatch(`/subjects/${record.id}/reject`, {
                    reason: null,
                }),
            successMessage: "Subject rejeté",
        });
    };

    return (
        <Tooltip title="Rejeter ce subject">
      <span>
        <Button
            onClick={onClick}
            disabled={loading}
            variant="contained"
            size="small"
            sx={{
                bgcolor: "#d32f2f",
                color: "white",
                borderRadius: "18px",
                textTransform: "none",
                px: 1.5,
                py: 0.25,
                "&:hover": { bgcolor: "#b71c1c" },
            }}
            startIcon={
                loading ? (
                    <CircularProgress size={14} sx={{ color: "white" }} />
                ) : (
                    <BlockIcon fontSize="small" />
                )
            }
        >
          Rejeter
        </Button>
      </span>
        </Tooltip>
    );
}

/* ================= Filtres RA ================= */
const SubjectFilters = [
    <SelectInput
        key="status"
        source="status"
        label="Statut"
        alwaysOn
        choices={[
            { id: "PENDING", name: "PENDING" },
            { id: "VALIDATED", name: "VALIDATED" },
            { id: "REJECTED", name: "REJECTED" },
        ]}
    />,
];

/* ================= Page ================= */
export default function SubjectList() {
    const searchFilter = useResourceSearchFilter("subjects");

    return (
        <>
            <ResourceStatusHeader
                resource="subjects"
                title="Subjects"
                subtitle="Totaux par statut"
            />
            <List
                exporter={false}
                perPage={25}
                sort={{ field: "createdAt", order: "DESC" }}
                filters={SubjectFilters}
                filter={searchFilter}
                title="Subjects"
            >
                <Datagrid bulkActionButtons={false}>
                    <TextField source="id" label="ID" />
                    <TextField source="subjectId" label="UUID" />

                    <TextField source="subject" label="Subject" />
                    <TextField source="shortSubject" label="Short" />

                    <TextField source="username" label="Auteur" />
                    <StatusField source="status" label="Statut" />
                    <DateField source="createdAt" label="Créé le" showTime />

                    {/* Actions subject — affichage conditionnel inside components */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <ValidateSubjectBtn />
                        <RejectSubjectBtn />
                    </Box>
                </Datagrid>
            </List>
        </>
    );
}
